export type RetailBarcodeFormat = "EAN13" | "EAN8" | "UPC" | "CODE128";

export type PrintDensity = "light" | "normal" | "dark";

export interface BarcodeCalibration {
  horizontalOffsetMm: number;
  verticalOffsetMm: number;
  gapMm: number;
  density: PrintDensity;
}

export const DEFAULT_BARCODE_CALIBRATION: BarcodeCalibration = {
  horizontalOffsetMm: 0,
  verticalOffsetMm: 0,
  gapMm: 2,
  density: "normal",
};

const CALIBRATION_STORAGE_PREFIX = "retailpulse:barcode-calibration:v1";

function clampNumber(value: unknown, minimum: number, maximum: number, fallback: number) {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.min(maximum, Math.max(minimum, parsed)) : fallback;
}

export function normalizeBarcodeCalibration(value: Partial<BarcodeCalibration> | null | undefined): BarcodeCalibration {
  return {
    horizontalOffsetMm: clampNumber(value?.horizontalOffsetMm, -10, 10, 0),
    verticalOffsetMm: clampNumber(value?.verticalOffsetMm, -10, 10, 0),
    gapMm: clampNumber(value?.gapMm, 0, 20, 2),
    density: value?.density === "light" || value?.density === "dark" ? value.density : "normal",
  };
}

export function getBarcodeCalibrationStorageKey(storeId: string | number, size: string) {
  return `${CALIBRATION_STORAGE_PREFIX}:${encodeURIComponent(String(storeId))}:${size}`;
}

type CalibrationStorage = Pick<Storage, "getItem" | "setItem">;

export function loadBarcodeCalibration(storage: CalibrationStorage, storeId: string | number, size: string) {
  try {
    const saved = storage.getItem(getBarcodeCalibrationStorageKey(storeId, size));
    return saved ? normalizeBarcodeCalibration(JSON.parse(saved)) : DEFAULT_BARCODE_CALIBRATION;
  } catch {
    return DEFAULT_BARCODE_CALIBRATION;
  }
}

export function saveBarcodeCalibration(storage: CalibrationStorage, storeId: string | number, size: string, calibration: BarcodeCalibration) {
  const normalized = normalizeBarcodeCalibration(calibration);
  storage.setItem(getBarcodeCalibrationStorageKey(storeId, size), JSON.stringify(normalized));
  return normalized;
}

export function getBarcodeDensityColor(density: PrintDensity) {
  if (density === "light") return "#444444";
  if (density === "dark") return "#000000";
  return "#111111";
}

function hasValidGs1CheckDigit(value: string): boolean {
  if (!/^\d+$/.test(value) || value.length < 2) return false;

  const digits = value.split("").map(Number);
  const suppliedCheckDigit = digits.pop()!;
  const weightedSum = digits
    .reverse()
    .reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 3 : 1), 0);
  const expectedCheckDigit = (10 - (weightedSum % 10)) % 10;

  return suppliedCheckDigit === expectedCheckDigit;
}

export function detectBarcodeFormat(value: string): RetailBarcodeFormat {
  const trimmed = value.trim();
  if (trimmed.length === 13 && hasValidGs1CheckDigit(trimmed)) return "EAN13";
  if (trimmed.length === 12 && hasValidGs1CheckDigit(trimmed)) return "UPC";
  if (trimmed.length === 8 && hasValidGs1CheckDigit(trimmed)) return "EAN8";
  return "CODE128";
}

export function getQuietZoneMargins(format: RetailBarcodeFormat, moduleWidth: number) {
  if (format === "EAN13") {
    return { marginLeft: Math.ceil(moduleWidth * 11), marginRight: Math.ceil(moduleWidth * 7) };
  }
  if (format === "EAN8") {
    return { marginLeft: Math.ceil(moduleWidth * 7), marginRight: Math.ceil(moduleWidth * 7) };
  }
  if (format === "UPC") {
    return { marginLeft: Math.ceil(moduleWidth * 9), marginRight: Math.ceil(moduleWidth * 9) };
  }
  return { marginLeft: Math.ceil(moduleWidth * 10), marginRight: Math.ceil(moduleWidth * 10) };
}

export function getBarcodeFormatLabel(format: RetailBarcodeFormat) {
  if (format === "EAN13") return "EAN-13";
  if (format === "EAN8") return "EAN-8";
  if (format === "UPC") return "UPC-A";
  return "Code 128";
}